import { Link } from "@tanstack/react-router";
import { useMemo } from "react";
import { Layout } from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { NumberRow } from "@/components/NumberPill";
import { GameLogo } from "@/components/GameLogo";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useGameBySlug, useResults, useStateBySlug } from "@/hooks/use-query-hooks";
import { formatDate } from "@/lib/format";
import { History, ChevronLeft, ChevronRight } from "lucide-react";
import { RouteNotFound } from "@/components/RouteNotFound";
import { gamePaths } from "@/lib/game-routing";

export const HISTORY_PAGE_SIZE = 50;
const HISTORY_LIMIT = 500;

/** Untyped Link wrapper: hrefs are built as strings for both game URL shapes. */
const A = Link as unknown as React.ComponentType<Record<string, unknown>>;

export function GameHistoryView({
  gameSlug,
  stateSlug,
  page: rawPage,
}: {
  gameSlug: string;
  stateSlug?: string;
  page: number;
}) {
  const paths = gamePaths(gameSlug, stateSlug);
  const page = Math.max(1, Math.floor(rawPage || 1));
  const { data: state } = useStateBySlug(stateSlug);
  const { data: game, isLoading: gameLoading, isFetched: gameFetched } = useGameBySlug(gameSlug, state?.id);
  const { data: results, isLoading } = useResults(game?.id, HISTORY_LIMIT);
  const count = results?.length ?? 0;
  const totalPages = Math.max(1, Math.ceil(count / HISTORY_PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageRows = useMemo(() => {
    if (!results) return [];
    const start = (safePage - 1) * HISTORY_PAGE_SIZE;
    return results.slice(start, start + HISTORY_PAGE_SIZE);
  }, [results, safePage]);

  if (!gameLoading && gameFetched && !game) {
    return (
      <Layout>
        <RouteNotFound gameSlug={gameSlug} />
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container py-6 md:py-10">
        <Breadcrumb className="mb-4">
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink asChild><Link to="/">Home</Link></BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <A to={paths.game}>{game?.name ?? gameSlug}</A>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>History</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        <div className="flex items-start gap-4 mb-6">
          {game && <GameLogo gameSlug={game.slug} gameName={game.name} logoUrl={game.logo_url} size="md" />}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <History className="h-6 w-6 text-primary" />
              <h1 className="font-display text-2xl md:text-3xl font-bold tracking-tight">
                {game?.name ?? "Game"} Winning Numbers History
              </h1>
            </div>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <span>{count} draws available</span>
              {game?.is_multistate && <Badge variant="secondary">Multi-State</Badge>}
            </div>
          </div>
        </div>

        <h2 className="font-sans text-lg font-semibold mb-3">Winning numbers archive</h2>
        <Card className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[160px]">Date</TableHead>
                <TableHead>Winning Numbers</TableHead>
                {game?.has_multiplier && <TableHead className="text-right w-[120px]">Multiplier</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading && (
                <TableRow><TableCell colSpan={3} className="py-6 text-center text-muted-foreground">Loading…</TableCell></TableRow>
              )}
              {!isLoading && count === 0 && (
                <TableRow><TableCell colSpan={3} className="py-6 text-center text-muted-foreground">No draws available yet.</TableCell></TableRow>
              )}
              {pageRows.map((r: any) => (
                <TableRow key={r.id}>
                  <TableCell className="whitespace-nowrap">
                    <A to={paths.result(r.draw_date)} className="text-primary hover:underline">
                      {formatDate(r.draw_date)}
                    </A>
                  </TableCell>
                  <TableCell>
                    <NumberRow numbers={r.winning_numbers || []} bonusNumbers={r.bonus_numbers || []} size="sm" nowrap align="left" />
                  </TableCell>
                  {game?.has_multiplier && (
                    <TableCell className="text-right text-muted-foreground">{r.multiplier ? `x${r.multiplier}` : "—"}</TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>

        {totalPages > 1 && (
          <nav className="mt-6 flex items-center justify-between gap-3" aria-label="Past draws pagination">
            {safePage > 1 ? (
              <Button variant="outline" size="sm" asChild>
                <A to={paths.history} search={{ page: safePage - 1 }} rel="prev">
                  <ChevronLeft className="h-4 w-4 mr-1" /> Newer draws
                </A>
              </Button>
            ) : (
              <span />
            )}
            <span className="text-sm text-muted-foreground">Page {safePage} of {totalPages}</span>
            {safePage < totalPages ? (
              <Button variant="outline" size="sm" asChild>
                <A to={paths.history} search={{ page: safePage + 1 }} rel="next">
                  Older draws <ChevronRight className="h-4 w-4 ml-1" />
                </A>
              </Button>
            ) : (
              <span />
            )}
          </nav>
        )}
      </div>
    </Layout>
  );
}
